import type { JSX } from 'react';
import { motion } from 'framer-motion';
import { revealItem, staggerParent, inViewOnce, useRevealGate } from './anim';

/**
 * A slim, FULL-BLEED "by the numbers" band right after the What-is pitch. Two jobs:
 *
 *  1. CONVERSION — quiet, honest proof that this is a real, rich world (not a thin demo): every figure is a
 *     true product fact (20 critters, a 70-sound board, unlimited rooms, zero installs). No fabricated user
 *     counts or logos — the product's anti-surveillance, no-vanity-metrics soul rules those out.
 *  2. RHYTHM — a tinted full-width band breaks the run of centred navy sections into a distinct "chapter",
 *     giving the eye a landmark as it scrolls.
 *
 * Full-bleed because the `<section>` itself spans the page width (only its inner container is max-width'd),
 * so a background on the section reaches edge-to-edge with no negative-margin trickery. Motion is the shared
 * gated reveal; the numbers rise in on a stagger and hold still under reduced motion.
 */
const STATS: { value: string; label: string }[] = [
  { value: '∞', label: 'Rooms you can open' },
  { value: '20', label: 'Critters to be' },
  { value: '70', label: 'Soundboard sounds' },
  { value: '0', label: 'Downloads, ever' },
];

export function ProofBand(): JSX.Element {
  const reduce = useRevealGate();
  return (
    <section aria-label="Duck Duck Space by the numbers" className="relative w-full border-y border-white/10 bg-white/[0.015]">
      {/* Soft warm wash from the top edge so the band glows a little without stealing focus. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(90% 130% at 50% 0%, rgba(255,193,58,0.08), transparent 62%)' }}
      />
      <motion.ul
        variants={reduce ? undefined : staggerParent}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="relative mx-auto grid max-w-6xl grid-cols-2 gap-y-10 px-4 py-14 sm:px-6 md:grid-cols-4 lg:px-8"
      >
        {STATS.map((s) => (
          <motion.li key={s.label} variants={reduce ? undefined : revealItem} className="text-center">
            <div className="bg-gradient-to-b from-amber-200 to-amber-400 bg-clip-text text-5xl font-extrabold tracking-tight text-transparent drop-shadow-[0_2px_20px_rgba(255,193,58,0.25)] sm:text-6xl">
              {s.value}
            </div>
            <div className="mt-2 text-sm font-medium text-white/60">{s.label}</div>
          </motion.li>
        ))}
      </motion.ul>
    </section>
  );
}
